(() => {
  if (window.FinstarkProductCards?.initialized) return;

  const CARD_SELECTOR = '[data-finstark-product-card]';
  const SWATCH_SELECTOR = '[data-finstark-card-swatch]';
  const SIZE_SELECTOR = '[data-finstark-variant-id]';

  class FinstarkProductCardController {
    constructor() {
      this.initialized = false;
      this.onPointerOver = this.onPointerOver.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onClick = this.onClick.bind(this);
    }

    start() {
      if (this.initialized) return;

      this.initialized = true;
      document.addEventListener('pointerover', this.onPointerOver);
      document.addEventListener('focusin', this.onFocusIn);
      document.addEventListener('click', this.onClick);
    }

    onPointerOver(event) {
      if (event.pointerType === 'touch') return;
      if (!(event.target instanceof Element)) return;

      const swatch = event.target.closest(SWATCH_SELECTOR);
      if (!swatch) return;

      if (
        event.relatedTarget instanceof Node &&
        swatch.contains(event.relatedTarget)
      ) {
        return;
      }

      const card = swatch.closest(CARD_SELECTOR);
      if (!card) return;

      this.selectColor(card, swatch);
    }

    onFocusIn(event) {
      if (!(event.target instanceof Element)) return;

      const swatch = event.target.closest(SWATCH_SELECTOR);
      if (!swatch) return;

      const card = swatch.closest(CARD_SELECTOR);
      if (!card) return;

      this.selectColor(card, swatch);
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;

      const sizeButton = event.target.closest(SIZE_SELECTOR);
      if (!sizeButton) return;

      const card = sizeButton.closest(CARD_SELECTOR);
      if (!card) return;

      event.preventDefault();
      event.stopPropagation();

      this.addVariant(card, sizeButton);
    }

    selectColor(card, swatch) {
      const productId = swatch.dataset.finstarkLinkedProductId;
      if (!productId) return;

      const productUrl =
        swatch.dataset.finstarkLinkedProductUrl || '#';

      const productTitle =
        swatch.dataset.finstarkLinkedProductTitle || '';

      const colorName =
        swatch.dataset.finstarkLinkedColorName || '';

      const primaryUrl =
        swatch.dataset.finstarkLinkedPrimaryImage || '';

      const primarySrcset =
        swatch.dataset.finstarkLinkedPrimarySrcset || '';

      const secondaryUrl =
        swatch.dataset.finstarkLinkedSecondaryImage || '';

      const secondarySrcset =
        swatch.dataset.finstarkLinkedSecondarySrcset || '';

      const imageAlt =
        swatch.dataset.finstarkLinkedImageAlt || productTitle;

      card.querySelectorAll(SWATCH_SELECTOR).forEach((item) => {
        const selected = item === swatch;

        item.classList.toggle('is-active', selected);

        if (selected) {
          item.setAttribute('aria-current', 'true');
        } else {
          item.removeAttribute('aria-current');
        }
      });

      card
        .querySelectorAll('[data-finstark-card-link]')
        .forEach((link) => {
          link.href = productUrl;

          if (link.classList.contains('fspc__image-link')) {
            link.setAttribute('aria-label', productTitle);
          }
        });

      card
        .querySelectorAll('[data-finstark-card-title]')
        .forEach((title) => {
          title.textContent = productTitle;
        });

      const colorNameElement = card.querySelector(
        '[data-finstark-card-color-name]'
      );

      if (colorNameElement) {
        colorNameElement.textContent = colorName;
        colorNameElement.hidden = !colorName;
      }

      this.updateImage(
        card.querySelector('[data-finstark-card-primary-image]'),
        primaryUrl,
        primarySrcset,
        imageAlt
      );

      this.updateSecondaryImage(
        card.querySelector('[data-finstark-card-secondary-image]'),
        secondaryUrl,
        secondarySrcset,
        imageAlt
      );

      this.replaceTemplate(
        card,
        '[data-finstark-card-price]',
        'data-finstark-card-price-template',
        productId
      );

      const sizeUpdated = this.replaceTemplate(
        card,
        '[data-finstark-card-quick-size-slot]',
        'data-finstark-card-size-template',
        productId
      );

      const sizeSlot = card.querySelector(
        '[data-finstark-card-quick-size-slot]'
      );

      if (sizeSlot) {
        sizeSlot.hidden =
          !sizeUpdated || !sizeSlot.innerHTML.trim();
      }

      this.replaceTemplate(
        card,
        '[data-finstark-card-badges]',
        'data-finstark-card-badge-template',
        productId
      );

      card.dataset.finstarkCurrentProductId = productId;
      card.classList.add('is-finstark-color-selected');

      card.dispatchEvent(
        new CustomEvent('finstark:card-product-change', {
          bubbles: true,
          detail: {
            productId,
            productUrl,
            productTitle,
            colorName,
          },
        })
      );
    }

    updateImage(image, url, srcset, alt) {
      if (!image || !url) return;

      image.src = url;

      if (srcset) {
        image.srcset = srcset;
      } else {
        image.removeAttribute('srcset');
      }

      image.alt = alt;
      image.hidden = false;
    }

    updateSecondaryImage(image, url, srcset, alt) {
      if (!image) return;

      if (!url) {
        image.hidden = true;
        return;
      }

      image.src = url;

      if (srcset) {
        image.srcset = srcset;
      } else {
        image.removeAttribute('srcset');
      }

      image.alt = alt;
      image.hidden = false;
    }

    replaceTemplate(
      card,
      targetSelector,
      templateAttribute,
      productId
    ) {
      const target = card.querySelector(targetSelector);
      if (!target) return false;

      const template = card.querySelector(
        `[${templateAttribute}="${productId}"]`
      );

      if (!template) return false;

      target.innerHTML = template.innerHTML;

      return true;
    }

    async addVariant(card, button) {
      if (
        card.dataset.finstarkAdding === 'true' ||
        button.disabled
      ) {
        return;
      }

      const variantId = Number(
        button.dataset.finstarkVariantId
      );

      if (!variantId) return;

      const status = card.querySelector(
        '[data-finstark-card-status]'
      );

      const sizeSlot = card.querySelector(
        '[data-finstark-card-quick-size-slot]'
      );

      const cart =
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-notification');

      const sectionIds = this.getCartSectionIds(cart);

      card.dataset.finstarkAdding = 'true';
      card.setAttribute('aria-busy', 'true');

      sizeSlot?.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      card.querySelectorAll(SIZE_SELECTOR).forEach(
        (sizeButton) => {
          sizeButton.disabled = true;
        }
      );

      if (status) {
        status.textContent = 'Adding item to cart';
      }

      try {
        const requestBody = {
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        };

        if (sectionIds.length) {
          requestBody.sections = sectionIds;
          requestBody.sections_url =
            window.location.pathname;
        }

        const response = await fetch(
          this.getCartAddUrl(),
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(requestBody),
          }
        );

        const result = await response
          .json()
          .catch(() => ({}));

        if (!response.ok || result.status) {
          throw new Error(
            result.description ||
              result.message ||
              'Unable to add this item'
          );
        }

        if (!result.id) {
          result.id = variantId;
        }

        if (
          cart &&
          typeof cart.renderContents === 'function'
        ) {
          await this.ensureCartSections(
            result,
            sectionIds
          );

          this.renderCart(cart, result, button);
        } else {
          window.location.assign(this.getCartUrl());
          return;
        }

        if (status) {
          status.textContent = 'Item added to cart';
        }
      } catch (error) {
        console.error(
          '[Finstark product card]',
          error
        );

        if (status) {
          status.textContent =
            error.message ||
            'Unable to add this item';
        }
      } finally {
        delete card.dataset.finstarkAdding;

        card.removeAttribute('aria-busy');
        sizeSlot?.classList.remove('is-loading');
        button.removeAttribute('aria-busy');

        card.querySelectorAll(SIZE_SELECTOR).forEach(
          (sizeButton) => {
            if (
              !sizeButton.classList.contains(
                'is-unavailable'
              )
            ) {
              sizeButton.disabled = false;
            }
          }
        );
      }
    }

    getCartSectionIds(cart) {
      if (
        !cart ||
        typeof cart.getSectionsToRender !== 'function'
      ) {
        return [];
      }

      return [
        ...new Set(
          cart
            .getSectionsToRender()
            .map(
              (section) =>
                section.id || section.section
            )
            .filter(Boolean)
        ),
      ];
    }

    async ensureCartSections(result, sectionIds) {
      if (!sectionIds.length) return;

      const hasEverySection =
        result.sections &&
        sectionIds.every(
          (sectionId) =>
            typeof result.sections[sectionId] ===
            'string'
        );

      if (hasEverySection) return;

      const sectionsUrl = new URL(
        window.location.href
      );

      sectionsUrl.search = '';

      sectionsUrl.searchParams.set(
        'sections',
        sectionIds.join(',')
      );

      const response = await fetch(
        sectionsUrl.toString(),
        {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          'The item was added, but the cart drawer could not refresh'
        );
      }

      result.sections = await response.json();
    }

    renderCart(cart, result, button) {
      cart.classList.remove('is-empty');

      cart
        .querySelector('.drawer__inner')
        ?.classList.remove('is-empty');

      cart.setActiveElement?.(button);
      cart.renderContents(result);

      if (cart.matches('cart-drawer')) {
        window.setTimeout(() => {
          if (!cart.classList.contains('active')) {
            cart.open?.(button);
          }
        }, 30);
      }
    }

    getCartAddUrl() {
      if (window.routes?.cart_add_url) {
        return window.routes.cart_add_url;
      }

      const root =
        window.Shopify?.routes?.root || '/';

      return `${root}cart/add.js`;
    }

    getCartUrl() {
      if (window.routes?.cart_url) {
        return window.routes.cart_url;
      }

      const root =
        window.Shopify?.routes?.root || '/';

      return `${root}cart`;
    }
  }

  window.FinstarkProductCards =
    new FinstarkProductCardController();

  window.FinstarkProductCards.start();
})();